import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ExpandCollapseCell from 'src/components/custom-cells/ExpandCollapseCell';
import Button from 'src/components/design-system/Button';
import { MESSAGE_MODE } from 'src/constants/messageMode';
import { MESSAGE_STATUS, SPECIAL_SENDER } from 'src/constants/messageStatus';
import useAuth from 'src/hooks/useAuth';
import useCompany from 'src/hooks/useCompany';
import useConfirm from 'src/hooks/useConfirm';
import useViewMessage from 'src/hooks/useViewMessage';
import useDeleteMessagesApi from 'src/services/messages_services/useDeleteMessagesApi';
import { Message } from 'src/services/messages_services/useGetMessageApi';
import useGetMessageAttachmentsApi from 'src/services/messages_services/useGetMessageAttachmentsApi';
import useMessageDetailsOutboxApi, {
  MessageDetailsOutboxResponse,
} from 'src/services/messages_services/useMessageDetailsOutboxApi';
import { toApiError } from 'src/utils/errorHandling';
import BaseMessageDetailsModal from './BaseMessageDetailsModal';

interface MessageDetailsOutboxModalProps {
  visible: boolean;
  closeModal: () => void;
  message: Message | null;
}

const MessageDetailsOutboxModal: FC<MessageDetailsOutboxModalProps> = ({
  visible,
  closeModal,
  message,
}) => {
  const { t } = useTranslation();
  const { company } = useCompany();
  const { clientId: authClientId } = useAuth();
  const { confirm } = useConfirm();
  const { refreshTable } = useViewMessage();
  const [messageDetails, setMessageDetails] = useState<MessageDetailsOutboxResponse | null>(null);
  const [isContactInfoExpanded, setIsContactInfoExpanded] = useState(false);

  // Reset expansion state when modal opens/closes
  useEffect(() => {
    if (visible) {
      // Reset to collapsed when modal opens
      setIsContactInfoExpanded(false);
    }
  }, [visible]);

  // Initialize API hooks for delete functionality
  const clientIdForDelete = authClientId || company?.companyId || '';
  const messageIdsForDelete = message?.messageId ? [message.messageId] : [];
  const { deleteMessage } = useDeleteMessagesApi(
    clientIdForDelete,
    messageIdsForDelete,
    true,
    MESSAGE_MODE.OUTBOX,
    message ? [message] : []
  );

  // Use separate API for getting attachment filenames
  const { getMessageAttachments, clearAttachments } = useGetMessageAttachmentsApi();

  // Use outbox-specific message details API
  const messageIdString = message?.messageId?.toString();
  const shouldUseApi =
    visible && messageIdString && messageIdString !== 'undefined' && messageIdString !== 'null';

  const { getMessage: getMessageDetails } = useMessageDetailsOutboxApi(
    shouldUseApi ? messageIdString! : 'NO_MESSAGE'
  );

  // Fetch message details when modal opens
  useEffect(() => {
    let isMounted = true;

    const fetchDetails = async () => {
      // Early exit if conditions not met
      if (!visible || !message?.messageId || !shouldUseApi) {
        setMessageDetails(null);
        return;
      }

      try {
        // Clear previous details before fetching new ones
        setMessageDetails(null);

        const details = await getMessageDetails();

        if (isMounted && details) {
          setMessageDetails(details);
        }
      } catch (error: unknown) {
        const apiError = toApiError(error);

        if (isMounted && visible) {
          if (apiError.name === 'AbortError' || apiError.message.includes('Request cancelled')) {
            return;
          }

          console.warn('Failed to fetch outbox message details:', apiError.message);
          setMessageDetails(null);
        }
      }
    };

    if (visible && message?.messageId && shouldUseApi) {
      fetchDetails();
    } else {
      setMessageDetails(null);
    }

    return () => {
      isMounted = false;
    };
  }, [visible, message?.messageId, shouldUseApi, getMessageDetails]);

  // Fetch attachments when modal opens
  useEffect(() => {
    if (!message) return;

    if (visible && message.attachmentsCount > 0 && message.messageId) {
      getMessageAttachments(message.messageId);
    } else {
      clearAttachments();
    }

    return () => {
      clearAttachments();
    };
  }, [visible, message, getMessageAttachments, clearAttachments]);

  // Delete handler for outbox
  const handleDeleteMessage = useCallback(async () => {
    if (!message) return;

    const confirmed = await confirm({
      content: t('Are you sure you want to delete this message?'),
      title: t('Confirm Delete'),
    });

    if (!confirmed) return;

    try {
      await deleteMessage();
      toast.success(t('Message deleted successfully'));
      closeModal();
      refreshTable();
    } catch (error: unknown) {
      const apiError = toApiError(error);
      console.error('Delete message failed:', apiError);
      const errorMessage =
        apiError.response?.data?.message || apiError.message || t('Failed to delete message');
      toast.error(errorMessage);
    }
  }, [message, confirm, t, deleteMessage, closeModal, refreshTable]);

  // Toggle handler for recipient contact info
  const handleToggleContactInfo = useCallback(() => {
    setIsContactInfoExpanded((prev) => !prev);
  }, []);

  if (!message) return null;

  const lowercaseStatus = message.decodedStatus.toLowerCase();
  const lowercaseCreatedBy = message.createdByUserName.toLowerCase();

  // Consolidate recipient info fallback chains to handle API inconsistencies
  const getRecipientInfo = (details: MessageDetailsOutboxResponse | null, msg: Message) => ({
    fullName: details?.recipientFullName || details?.toFullName || msg?.recipient || t('N/A'),
    phone: details?.recipientPhone || details?.recipientTelecom || details?.toPhone || t('N/A'),
    email: t('N/A'), // Not available
    company: msg?.recipientCompanyName || t('N/A'),
  });

  const recipientInfo = getRecipientInfo(messageDetails, message);

  // Action buttons for outbox - show delete button unless already deleted
  const renderActions = () => {
    // Don't show delete button if message is already deleted
    if (
      lowercaseStatus === MESSAGE_STATUS.DELETED ||
      lowercaseStatus === MESSAGE_STATUS.DELETED_BY_SENDER
    ) {
      return null;
    }

    return (
      <>
        <Button color="error" size="md" onClick={handleDeleteMessage}>
          {t('Delete')}
        </Button>
      </>
    );
  };

  // Render contact info (recipient details) for outbox
  const renderContactInfo = () => (
    <>
      {/* From/To Information */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-blue-800 mb-1">{t('From')}</label>
          <div className="text-gray-900">{message.createdByUserName}</div>
        </div>
        <div className="flex flex-col gap-4">
          <div>
            <label className="message-label">{t('To')}</label>
            <div className="flex items-center gap-2">
              <span className="text-gray-900">{message.recipient}</span>
              <ExpandCollapseCell
                expand={isContactInfoExpanded}
                onExpand={handleToggleContactInfo}
                plusIconColor="#1e40af"
                minusIconColor="#1e40af"
                title={
                  isContactInfoExpanded
                    ? t('Click to collapse details')
                    : t('Click to expand details')
                }
              />
            </div>
          </div>
          <div>
            <label className="message-label">{t('To Company')}</label>
            <div className="text-gray-900">{message.recipientCompanyName}</div>
          </div>
        </div>

        {/* Recipient Contact Information - Conditionally Visible */}
        {isContactInfoExpanded && (
          <div
            className={`p-4 ${
              lowercaseCreatedBy === SPECIAL_SENDER.SYSTEM_PROCESS ? 'bg-transparent' : 'bg-blue-50'
            } border border-blue-200 rounded-md`}
            style={{ maxWidth: '550px' }}
          >
            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-xs font-medium text-blue-800 mb-1">
                  {t('Recipient Full Name')}
                </label>
                <div className="text-gray-900 text-sm">{recipientInfo.fullName}</div>
              </div>
              <div>
                <label className="block text-xs font-medium text-blue-800 mb-1">
                  {t('Recipient Email')}
                </label>
                <div className="text-gray-900 text-sm">{recipientInfo.email}</div>
              </div>
              <div>
                <label className="block text-xs font-medium text-blue-800 mb-1">
                  {t('Recipient Phone Number')}
                </label>
                <div className="text-gray-900 text-sm">{recipientInfo.phone}</div>
              </div>
              <div>
                <label className="block text-xs font-medium text-blue-800 mb-1">
                  {t('Recipient Company')}
                </label>
                <div className="text-gray-900 text-sm">{recipientInfo.company}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );

  // Render additional content (attachments) for outbox
  const renderAdditionalContent = () => null;

  return (
    <BaseMessageDetailsModal
      visible={visible}
      closeModal={closeModal}
      message={message}
      mode={MESSAGE_MODE.OUTBOX}
      title={
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faEnvelope} className="text-white" />
          {t('Outbox Message Details')}
        </div>
      }
      currentStatus={message.decodedStatus}
      isContactInfoExpanded={isContactInfoExpanded}
      renderActions={renderActions}
      renderContactInfo={renderContactInfo}
      renderAdditionalContent={renderAdditionalContent}
    />
  );
};

export default MessageDetailsOutboxModal;
